import type { BadgeVariations } from '@synqo/frontend-component-library';
import { EnumActionEventCategory } from './Generated';

/**
 * Translates an action event string to a German display label.
 * Handles two patterns:
 * - Domain events: entity.{action} (e.g., entity.created, entity.updated, entity.deleted)
 * - Business events: action.{domain}.{action}.v1 (e.g., action.email.sent.v1, action.staging.imported.v1)
 */
export const getActionEventLabel = (action: string): string => {
    if (!action) {
        return 'Unbekannt';
    }

    // Handle domain events (entity.{action})
    if (action.startsWith('entity.')) {
        const actionPart = action.replace('entity.', '');
        return translateEntityAction(actionPart);
    }

    // Handle business events ({domain}.{action}.v1)

    const parts = action.split('.');
    if (parts.length >= 2) {
        const domain = parts[0];
        const actionPart = parts[1];
        const domainLabel = translateDomain(domain);
        const actionLabel = translateBusinessAction(actionPart);
        return `${domainLabel}: ${actionLabel}`;
    }

    // Fallback: return as-is
    return action;
};

// @ai-generated 2026-07-01T08:58:41+00:00 by malte.ulfers@tngtech.com using Claude Opus 4.7 (1M context)
export const getEnumActionEventCategoryLabel = (category: EnumActionEventCategory): string => {
    switch (category) {
        case EnumActionEventCategory.Success:
            return 'Erfolg';
        case EnumActionEventCategory.Warning:
            return 'Warnung';
        case EnumActionEventCategory.Error:
            return 'Fehler';
        case EnumActionEventCategory.Notice:
            return 'Hinweis';
        case EnumActionEventCategory.Info:
        default:
            return 'Info';
    }
};

// @ai-generated 2026-07-01T08:58:41+00:00 by malte.ulfers@tngtech.com using Claude Opus 4.7 (1M context)
export const getBadgeVariantByCategory = (category: EnumActionEventCategory): BadgeVariations => {
    switch (category) {
        case EnumActionEventCategory.Success:
            return 'success';
        case EnumActionEventCategory.Warning:
            return 'warning';
        case EnumActionEventCategory.Error:
            return 'danger';
        case EnumActionEventCategory.Notice:
            return 'info';
        case EnumActionEventCategory.Info:
        default:
            return 'default';
    }
};

/**
 * Translates entity action verbs to German
 */
const translateEntityAction = (action: string): string => {
    switch (action.toLowerCase()) {
        case 'inserted':
            return 'Erstellt';
        case 'updated':
            return 'Aktualisiert';
        case 'deleted':
            return 'Gelöscht';
        default:
            return action;
    }
};

/**
 * Translates business action verbs to German
 */
const translateBusinessAction = (action: string): string => {
    switch (action.toLowerCase()) {
        case 'sent':
            return 'Gesendet';
        case 'delivered':
            return 'Zugestellt';
        case 'failed':
            return 'Fehlgeschlagen';
        case 'new':
            return 'Neu';
        case 'seen':
            return 'Gesehen';
        case 'imported':
            return 'Importiert';
        case 'rejected':
            return 'Abgelehnt';
        case 'succeeded':
            return 'Erfolgreich';
        case 'scheduled':
            return 'Geplant';
        case 'running':
            return 'Läuft';
        case 'finished':
            return 'Abgeschlossen';
        case 'block':
            return 'Gesperrt';
        case 'password_reset':
            return 'Passwort zurückgesetzt';
        case 'partnership_accepted':
            return 'Partnerschaft angenommen';
        case 'partnership_rejected':
            return 'Partnerschaft abgelehnt';
        default:
            return action;
    }
};

/**
 * Translates domain names to German
 */
const translateDomain = (domain: string): string => {
    switch (domain.toLowerCase()) {
        case 'email':
            return 'E-Mail';
        case 'staging':
            return 'Staging';
        case 'export':
            return 'Export';
        case 'invoice_batch_creation':
            return 'Rechnungserstellung';
        case 'container_rent_billing':
            return 'Mietabrechnung';
        case 'user':
            return 'Benutzer';
        default:
            return domain;
    }
};
